import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { tenantIDAtom } from "../../../../atoms/GlobalAtoms";
import { BrieConversation } from "../../../../components/BrieChatMenu/BrieMessages";
import { callFunctionIfExists } from "../../../../utils/GeneralUtils";
import BrieConversationAPI from "../../../api/BrieConversationAPI";
import { SearchGPTMessage } from "../../../../components/BrieChatMenu/GPTConvoTypes";
import { handleBrieCommands } from "./handleBrieCommands";
import { workspaceIdAtom } from "../../../../atoms/WorkspaceAtoms";
import {
  createRecordFamily,
  prefillFamily,
  queryAtomFamily,
} from "../../../../components/PageView/PageViewTable/SharedToolbar/SharedToolbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import useView from "../../views/useView";

// Helper function to add a message to a query client
const addMessageToConvo = async (
  queryClient: QueryClient,
  conversationID: string,
  newMessage: SearchGPTMessage | undefined
) => {
  if (!newMessage) return;

  let conversation = queryClient.getQueryData<BrieConversation | null>([
    "conversation",
    conversationID,
  ]);

  if (!conversation && conversationID !== "new") {
    conversation = await BrieConversationAPI.getGPTConversationByID(
      conversationID
    ).then((data) => data.data.data.conversation);
  }

  queryClient.setQueryData<BrieConversation | null>(
    ["conversation", conversationID],
    // @ts-ignore
    (prev) => {
      if (!prev) prev = conversation ? conversation : prev;
      if (!prev && conversationID !== "new") return prev;

      const newMessages = Array.isArray(prev?.Messages)
        ? structuredClone(prev?.Messages)
        : [];

      newMessages.push(newMessage);

      return { ...prev, Messages: newMessages };
    }
  );
};

interface UpdateGPTProps {
  conversationID: string;
  message: SearchGPTMessage;
  workspaceID?: string;
  onSuccess?: (conversationID: string) => void;
}

const empty: [] = [];

const useUpdateGPTConversation = ({
  pageID,
  viewID,
}: {
  pageID: string | undefined;
  viewID: string | undefined;
}) => {
  const { view } = useView({ pageID, viewID });
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const setQuery = useSetAtom(queryAtomFamily(viewID));
  const setCreateRecord = useSetAtom(createRecordFamily(viewID));
  const setPrefill = useSetAtom(prefillFamily(viewID));
  const navigate = useNavigate();
  const [, setSearch] = useSearchParams();

  const updateGPTConversation = useMutation({
    mutationFn: ({ conversationID, message, onSuccess }: UpdateGPTProps) =>
      BrieConversationAPI.updateGPTConversation({
        conversationID,
        message: message.Message,
        viewID,
        pageID,
        workspaceID: workspaceID!,
      }).then((data) => {
        const isNew = data.data.data.isNew;

        // Handle new conversation (API returns full conversation)
        if (isNew) {
          const newConversation = data.data.data.conversation;

          queryClient.setQueryData(
            ["conversation", newConversation?.id],
            newConversation
          );
          queryClient.setQueryData(["conversations-gpt", tenantID], (prev) => [
            ...(Array.isArray(prev) ? prev : []),
            newConversation,
          ]);

          // Set the conversationID to the new conversation ID that was generated
          // This is VERY important.
          callFunctionIfExists(onSuccess, newConversation?.id);
          handleBrieCommands({
            message: data?.data?.data?.responseMessage,
            setQuery,
            navigate,
            setSearch,
            prefills: view?.Prefills || empty,
            setCreateRecord,
            setPrefill,
          });

          return;
        } else {
          // Handle existing conversations (API returns just response message)
          const message: SearchGPTMessage | undefined =
            data?.data?.data?.responseMessage;
          const lastUpdated = data?.data?.data?.lastUpdated;

          queryClient.setQueryData<SearchGPTMessage[] | null>(
            ["conversations-gpt", tenantID],
            (prev) => {
              if (!Array.isArray(prev)) return;

              return prev.map((c) => {
                if (c.id === conversationID) {
                  return { ...c, LastUpdated: lastUpdated };
                }
                return c;
              });
            }
          );

          addMessageToConvo(queryClient, conversationID, message);
          handleBrieCommands({
            message,
            setQuery,
            navigate,
            setSearch,
            prefills: view?.Prefills || empty,
            setCreateRecord,
            setPrefill,
          });
        }
      }),
    onMutate: ({ conversationID, message }: UpdateGPTProps) => {
      // No query cache exists yet when it's a new conversation, so return
      if (conversationID === "new") return;

      addMessageToConvo(queryClient, conversationID, message);
    },
  });

  return {
    updateGPTConvo: updateGPTConversation.mutate,
    gptProcessing: updateGPTConversation.isPending,
    updateGPTConvoError: updateGPTConversation?.error,
  };
};

export default useUpdateGPTConversation;
